import { useState, useEffect, useRef, useCallback } from 'react'
import '@styles/App.css'
import '@styles/terminal.css'
import '@styles/effects.css'
import '@styles/tandy.css'
import '@styles/modern.css'
import Starfield from '@visual/Starfield'
import Terminal from '@components/Terminal'
import ModernPortfolio from '@components/ModernPortfolio'
import ThemeTransition from '@components/ThemeTransition'

type ThemeMode = 'modern' | 'professional' | 'horror' | 'tandy'

const getDefaultTheme = (): ThemeMode => {
  const saved = localStorage.getItem('terminal-theme') as ThemeMode | null
  if (saved) return saved
  return 'modern'
}

const isMobileViewport = () => window.innerWidth <= 768

function App() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(getDefaultTheme)
  const [isMobile, setIsMobile] = useState(isMobileViewport)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [isBooted, setIsBooted] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [transitioning, setTransitioning] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const loginInputRef = useRef<HTMLInputElement | null>(null)

  const handlePlayAudio = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play().catch(() => {})
        setIsPlaying(true)
      } else {
        audioRef.current.pause()
        setIsPlaying(false)
      }
      return
    }
    const audio = new Audio('/twelveson.mp3')
    audio.volume = 0.5
    audio.addEventListener('ended', () => setIsPlaying(false))
    audio.play().catch(() => {})
    audioRef.current = audio
    setIsPlaying(true)
    setIsMuted(false)
  }

  const toggleMute = () => {
    if (!audioRef.current) return
    const muted = !isMuted
    audioRef.current.muted = muted
    setIsMuted(muted)
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileViewport())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const mobileDefault = window.innerWidth <= 768 ? 'small' : 'medium'
    const savedFontSize = localStorage.getItem('terminal-font-size') || mobileDefault
    const savedContrast = localStorage.getItem('terminal-contrast') || 'normal'
    document.documentElement.setAttribute('data-font-size', savedFontSize)
    document.documentElement.setAttribute('data-contrast', savedContrast)
  }, [])

  const handleThemeChange = (theme: 'professional' | 'horror' | 'tandy') => {
    setThemeMode(theme)
    localStorage.setItem('terminal-theme', theme)
  }

  const handleEnterTerminal = useCallback(() => {
    setTransitioning(true)
  }, [])

  const handleTransitionComplete = useCallback(() => {
    setTransitioning(false)
    setThemeMode('professional')
    setIsLoggedIn(true)
    setIsBooted(true)
    localStorage.setItem('terminal-theme', 'professional')
  }, [])

  const handleBackToPortfolio = () => {
    setThemeMode('modern')
    localStorage.setItem('terminal-theme', 'modern')
  }

  const handleSkipToInput = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const input = document.getElementById('terminal-input') as HTMLInputElement
    if (input) {
      input.focus()
      input.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    setIsAuthenticating(true)
    requestAnimationFrame(() => {
      setTimeout(() => {
        setIsAuthenticating(false)
        setIsLoggedIn(true)
      }, 200)
    })
  }

  useEffect(() => {
    if (!isLoggedIn && !isAuthenticating && loginInputRef.current) {
      loginInputRef.current.focus()
    }
  }, [isLoggedIn, isAuthenticating])

  // Modern theme render path
  if (themeMode === 'modern') {
    return (
      <>
        <ThemeTransition active={transitioning} onComplete={handleTransitionComplete} />
        <ModernPortfolio onEnterTerminal={handleEnterTerminal} />
      </>
    )
  }

  // Terminal theme render path
  return (
    <div className={`app ${themeMode}-mode`} data-theme={themeMode} role="main">
      <a href="#terminal-input" className="skip-link" onClick={handleSkipToInput}>
        Skip to terminal input
      </a>

      {themeMode !== 'tandy' && (
        <Starfield intensity={themeMode === 'horror' ? 'high' : 'low'} />
      )}

      <div className="terminal-container" role="application" aria-label="Interactive portfolio terminal">
        <div className="crt-overlay" aria-hidden="true" />
        <div className="scanlines" aria-hidden="true" />
        <div className="vignette" aria-hidden="true" />

        <img
          src="/portfolio-logo.webp"
          alt="Anderson Reinkordt"
          className="portfolio-logo"
          aria-hidden="true"
        />

        {/* Back to portfolio button */}
        <button
          className="back-to-portfolio"
          onClick={handleBackToPortfolio}
          aria-label="Back to portfolio"
        >
          [PORTFOLIO]
        </button>

        <div className="terminal-bezel">
          <header className="terminal-header">
            <h1 className="terminal-title">ANDERSON_REINKORDT@PORTFOLIO:~$</h1>
          </header>

          <main className="terminal-content">
            {isMobile ? (
              <section className="mobile-message" aria-labelledby="mobile-heading">
                <h1 id="mobile-heading" className="sr-only">Desktop Required</h1>
                <pre className="mobile-message__ascii" aria-hidden="true">{`
   ▄▀█ █▄░█ █▀▄ █▀▀ █▀█ █▀ █▀█ █▄░█
   █▀█ █░▀█ █▄▀ ██▄ █▀▄ ▄█ █▄█ █░▀█

   █▀█ █▀▀ █ █▄░█ █▄▀ █▀█ █▀█ █▀▄ ▀█▀
   █▀▄ ██▄ █ █░▀█ █░█ █▄█ █▀▄ █▄▀ ░█░
`}</pre>
                <div className="mobile-message__content">
                  <p className="mobile-message__title">Best viewed on desktop</p>
                  <p className="mobile-message__text">
                    This interactive terminal portfolio is designed
                    for larger screens. Please visit from a desktop
                    or laptop for the full experience.
                  </p>
                  <p className="mobile-message__url">sonander.dev</p>
                </div>
              </section>
            ) : !isLoggedIn ? (
              <section className="login-screen" aria-labelledby="login-heading">
                <h1 id="login-heading" className="sr-only">System Login</h1>
                <pre className="login-header" aria-hidden="true">{`
┌─────────────────────────────────────────┐
│             can.code  v2.3              │
│              ━━━━━━━━━━━━               │
│         SYSTEM LOGIN REQUIRED           │
└─────────────────────────────────────────┘`}</pre>

                {!isAuthenticating ? (
                  <form className="login-form" onSubmit={handleLogin}>
                    <div className="login-field">
                      <label htmlFor="login-username" className="login-label">USERNAME:</label>
                      <input
                        id="login-username"
                        ref={loginInputRef}
                        type="text"
                        className="login-input"
                        placeholder="try anything..."
                        spellCheck={false}
                        autoComplete="off"
                        autoFocus
                      />
                    </div>
                    <div className="login-field">
                      <label htmlFor="login-password" className="login-label">PASSWORD:</label>
                      <input
                        id="login-password"
                        type="password"
                        className="login-input"
                        placeholder="...seriously, anything"
                        spellCheck={false}
                        autoComplete="off"
                      />
                    </div>
                    <button type="submit" className="login-button">
                      [ ACCESS SYSTEM ]
                    </button>
                    <p className="login-hint">hint: any credentials will work ;)</p>
                  </form>
                ) : (
                  <div className="login-status" role="status" aria-live="polite">
                    <p className="login-status-line">Verifying credentials...</p>
                    <p className="login-status-line login-status-granted">Access granted.</p>
                  </div>
                )}
              </section>
            ) : !isBooted ? (
              <section className="boot-sequence" aria-labelledby="boot-heading">
                <h1 id="boot-heading" className="sr-only">System Boot Sequence</h1>
                <pre className="ascii-logo" aria-hidden="true">
{`
   ▄▀█ █▄░█ █▀▄ █▀▀ █▀█ █▀ █▀█ █▄░█
   █▀█ █░▀█ █▄▀ ██▄ █▀▄ ▄█ █▄█ █░▀█

   █▀█ █▀▀ █ █▄░█ █▄▀ █▀█ █▀█ █▀▄ ▀█▀
   █▀▄ ██▄ █ █░▀█ █░█ █▄█ █▀▄ █▄▀ ░█░
`}
                </pre>
                <p className="sr-only">Anderson Reinkordt Portfolio Terminal</p>
                <div className="boot-text" role="status" aria-live="polite">
                  <h2 className="sr-only">Initialization Progress</h2>
                  <p>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                  <p>[✓] Initializing terminal interface...</p>
                  <p>[✓] Loading portfolio data...</p>
                  <p>[✓] Establishing connection...</p>
                  <p>[✓] Mounting application modules...</p>
                  <p>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                  <p className="ready-prompt">✦ System ready ✦</p>
                </div>
                <button
                  className="enter-button"
                  onClick={() => setIsBooted(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setIsBooted(true)
                    }
                  }}
                  aria-label="Press Enter to continue to terminal interface"
                  autoFocus
                >
                  [PRESS ENTER TO CONTINUE]
                </button>
              </section>
            ) : (
              <section className="main-interface" aria-labelledby="terminal-heading">
                <div className="welcome-text" role="banner">
                  <h1 id="terminal-heading" className="sr-only">Anderson Reinkordt Portfolio Terminal</h1>
                  <pre className="terminal-line" aria-hidden="true" style={{ margin: 0, lineHeight: '1.4' }}>{`╔════════════════════════════════════════════════════╗
║                                            __      ║
║   Welcome to Anderson Reinkordt's      ___( o)>    ║
║   Portfolio Terminal                   \\ <_. )     ║
║   AI Engineer | Accessible Intelligence \`---'      ║
╚════════════════════════════════════════════════════╝`}</pre>
                  <p className="terminal-line" aria-hidden="true"> </p>
                  <p className="terminal-line" aria-hidden="true">━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                  <p className="terminal-line" aria-label="Help instructions">▸ Type 'help' to see available commands</p>
                  <p className="terminal-line" aria-label="Tutorial tip">▸ Type 'tutorial' for an interactive guide</p>
                  <p className="terminal-line" aria-hidden="true">━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                  <p className="terminal-line" aria-hidden="true"> </p>
                </div>

                <Terminal onThemeChange={handleThemeChange} onPlayAudio={handlePlayAudio} />
              </section>
            )}
          </main>

          {isPlaying && (
            <button
              className="mute-toggle"
              onClick={toggleMute}
              aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
            >
              {isMuted ? '[MUTED]' : '[SOUND]'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default App
